/**
 * StopFileUploader — upload and display files attached to a route stop.
 *
 * Props:
 *   files        {Array}    - current files on the stop (url, name, type)
 *   onFilesChange {function} - called with updated files array
 *   baseUrl      {string}   - API base URL
 */

import React from 'react'
import axios from 'axios'
import { makeStyles } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Chip from '@material-ui/core/Chip'
import CircularProgress from '@material-ui/core/CircularProgress'
import IconButton from '@material-ui/core/IconButton'
import Tooltip from '@material-ui/core/Tooltip'
import Typography from '@material-ui/core/Typography'
import AttachFileIcon from '@material-ui/icons/AttachFile'
import DeleteIcon from '@material-ui/icons/Delete'
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile'

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(1),
  },
  uploadRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  fileGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
  },
  thumbWrapper: {
    position: 'relative',
    width: 80,
    height: 80,
    flexShrink: 0,
  },
  thumb: {
    width: 80,
    height: 80,
    objectFit: 'cover',
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    display: 'block',
  },
  thumbDelete: {
    position: 'absolute',
    top: -6,
    right: -6,
    padding: 2,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    '&:hover': {
      backgroundColor: theme.palette.error.light,
      color: theme.palette.error.contrastText,
    },
  },
  docChipWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  docChip: {
    maxWidth: 180,
  },
  emptyText: {
    color: theme.palette.text.disabled,
    fontSize: '0.78rem',
    fontStyle: 'italic',
  },
  spinner: {
    marginLeft: theme.spacing(1),
  },
}))

const IMAGE_MIMES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/webp', 'image/heic', 'image/tiff']
const ACCEPT = 'image/*,.pdf,.doc,.docx,.xls,.xlsx'

function isImageFile(file) {
  if (file.mimeType) return IMAGE_MIMES.includes(file.mimeType.toLowerCase())
  if (file.type) return IMAGE_MIMES.includes(file.type.toLowerCase())
  if (file.name) {
    const ext = file.name.split('.').pop().toLowerCase()
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'heic', 'tiff'].includes(ext)
  }
  return false
}

function getFileUrl(file) {
  return file.url || file.path || ''
}

function getFileName(file) {
  return file.name || file.originalname || file.filename || 'File'
}

function StopFileUploader({ files = [], onFilesChange, baseUrl = '' }) {
  const classes = useStyles()
  const inputRef = React.useRef(null)
  const [uploading, setUploading] = React.useState(false)
  const [error, setError] = React.useState(null)

  const handleButtonClick = () => {
    if (inputRef.current) inputRef.current.click()
  }

  const handleFileSelect = async (e) => {
    const selected = Array.from(e.target.files || [])
    if (!selected.length) return
    // Reset input so same file can be re-selected
    e.target.value = ''

    setError(null)
    setUploading(true)

    try {
      const formData = new FormData()
      selected.forEach((file) => {
        formData.append('theFileList', file)
      })

      const url = `${baseUrl}/api/uploads/uploadPhotos`
      const resp = await axios({
        method: 'POST',
        url,
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' },
      })

      const data = resp.data || {}
      // Uploaded files come back in `files` or `filesUploaded`
      const uploaded = data.files || data.filesUploaded || []
      if (uploaded.length) {
        const updated = [...files, ...uploaded]
        if (onFilesChange) onFilesChange(updated)
      } else if (data.errorFiles && data.errorFiles.length) {
        setError('Some files failed to upload.')
      }
    } catch (err) {
      console.error('[StopFileUploader] Upload error:', err)
      setError('Upload failed. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = (index) => {
    const updated = files.filter((_, i) => i !== index)
    if (onFilesChange) onFilesChange(updated)
  }

  const images = files.filter((f) => isImageFile(f))
  const docs = files.filter((f) => !isImageFile(f))

  return (
    <Box className={classes.root}>
      {/* Hidden file input */}
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPT}
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      {/* Upload button row */}
      <Box className={classes.uploadRow}>
        <Button
          variant="outlined"
          size="small"
          startIcon={uploading ? <CircularProgress size={14} /> : <AttachFileIcon />}
          onClick={handleButtonClick}
          disabled={uploading}
        >
          {uploading ? 'Uploading…' : 'Attach Files'}
        </Button>
        {error ? (
          <Typography variant="caption" color="error">
            {error}
          </Typography>
        ) : null}
      </Box>

      {/* File display area */}
      {files.length === 0 ? (
        <Typography className={classes.emptyText}>No files attached.</Typography>
      ) : (
        <Box className={classes.fileGrid}>
          {/* Photo thumbnails */}
          {images.map((file, i) => {
            const idx = files.indexOf(file)
            const src = getFileUrl(file)
            const name = getFileName(file)
            return (
              <Box key={`img-${i}`} className={classes.thumbWrapper}>
                <Tooltip title={name}>
                  <img
                    src={src}
                    alt={name}
                    className={classes.thumb}
                  />
                </Tooltip>
                <IconButton
                  size="small"
                  className={classes.thumbDelete}
                  onClick={() => handleDelete(idx)}
                  aria-label={`Remove ${name}`}
                >
                  <DeleteIcon style={{ fontSize: 12 }} />
                </IconButton>
              </Box>
            )
          })}

          {/* Document chips */}
          {docs.map((file, i) => {
            const idx = files.indexOf(file)
            const name = getFileName(file)
            const href = getFileUrl(file)
            return (
              <Box key={`doc-${i}`} className={classes.docChipWrapper}>
                <Chip
                  icon={<InsertDriveFileIcon />}
                  label={name}
                  size="small"
                  clickable={!!href}
                  component={href ? 'a' : 'div'}
                  href={href || undefined}
                  target={href ? '_blank' : undefined}
                  rel={href ? 'noopener noreferrer' : undefined}
                  className={classes.docChip}
                  onDelete={() => handleDelete(idx)}
                  deleteIcon={<DeleteIcon />}
                />
              </Box>
            )
          })}
        </Box>
      )}
    </Box>
  )
}

export default StopFileUploader
